import bugsnag from "@bugsnag/js";
import { Player } from "@lottiefiles/react-lottie-player";
import { AnimatePresence, motion } from "framer-motion";
import noop from "lodash/noop";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import BaseSelectionProvider from "../../../../../providers/selection/BaseSelectionProvider";
import BaseUploadProvider from "../../../../../providers/upload/BaseUploadProvider";
import { SelectionAlbum, SelectionPhoto } from "../../../../../types/selection/selection";
import { isPhotoInPicturesList } from "../../../../../utils/selection/finders";
import SelectionPagination from "../../../../atoms/selection/SelectionPagination";
import SelectionUploadButton from "../../../../atoms/selection/SelectionUploadButton";
import SelectionProviderBrowserPhoto from "../../../../molecules/selection/SelectionProviderBrowserPhoto";
import iconSpinner from "./icon-load.json";
import "./style.scss";

const MIN_PHOTOS_REQUIRED = 50;

interface Props {
  loading: boolean;
  maxSelectableItems?: number;
  onDisplayAlbumContent?: (albumKey: string) => void;
  onFileChange: (files: File[]) => Promise<void>;
  onSelect: (selectionPhoto: SelectionPhoto) => void;
  providerInstance: BaseSelectionProvider;
  selectedPhotoKeys: Array<{ key: string; providerRef: string }>;
  selectionPhotos?: SelectionPhoto[];
  uploadProvider?: BaseUploadProvider;
}

const UploadSummary: FunctionComponent<Props> = ({
  loading,
  maxSelectableItems,
  onDisplayAlbumContent,
  onFileChange,
  onSelect,
  providerInstance,
  uploadProvider,
  selectedPhotoKeys,
}) => {
  const intl = useIntl();

  const [albumsList, setAlbumsList] = useState<SelectionAlbum[]>([]);
  const [albumContent, setAlbumContent] = useState<SelectionPhoto[]>([]);
  const [pagination, setPagination] = useState<{
    display: boolean;
    onClick: () => void;
  }>({
    display: false,
    onClick: noop,
  });

  const resetPagination = (hasMore: boolean) => {
    if (!hasMore) {
      setPagination({ display: false, onClick: noop });
    }
  };

  const managePagination = (albumRef: string) => {
    if (providerInstance.hasMoreAlbumPhotos(albumRef)) {
      setPagination({
        display: true,
        onClick: () => {
          providerInstance
            .getMoreAlbumPhotos(albumRef)
            .then((morePhotosReceived) => {
              setAlbumContent(morePhotosReceived);
              resetPagination(providerInstance.hasMoreAlbumPhotos(albumRef));
            })
            .catch((e: Error) => {
              bugsnag.notify(e);
            });
        },
      });
    } else {
      setPagination({ display: false, onClick: noop });
    }
  };

  const fillWithPhotos = (currentAlbum: SelectionAlbum, isLast: boolean) => {
    if (providerInstance) {
      providerInstance
        .getAlbumPhotos(currentAlbum.ref)
        .then((photosReceived) => {
          if (onDisplayAlbumContent) {
            onDisplayAlbumContent(currentAlbum.ref);
          }
          if (isLast) {
            managePagination(currentAlbum.ref);
          }

          setAlbumContent((photosReceivedValue) => [...photosReceivedValue, ...photosReceived]);
        })
        .catch((e: Error) => {
          bugsnag.notify(e);
        });
    }
  };

  useEffect(() => {
    let isStillSubscribed = true;
    const today = new Date();
    const yearRgx = new RegExp(`^${today.getFullYear()}`);

    if (providerInstance.hasAlbums() && albumsList.length === 0) {
      providerInstance
        .getAlbums()
        .then((albumsReceived) => {
          if (!isStillSubscribed) return;
          setAlbumsList(albumsReceived);
          let nbPhotos = 0;

          // temporary hack to get more photos while API doesn't manage albums by year
          for (const album of albumsReceived) {
            if (nbPhotos < MIN_PHOTOS_REQUIRED && album.count && yearRgx.test(album.name)) {
              nbPhotos += album.count;
              fillWithPhotos(album, nbPhotos >= MIN_PHOTOS_REQUIRED);
            }
          }
        })
        .catch((e: Error) => {
          bugsnag.notify(e);
        });
    }
    return () => {
      isStillSubscribed = false;
    };
  }, []);

  const isPhotoSelected = (selectionPhoto: SelectionPhoto) => {
    return isPhotoInPicturesList(selectionPhoto, selectedPhotoKeys);
  };

  const finishedFiles = uploadProvider?.finished || [];
  const finishedFilesSelected = finishedFiles.filter((photo) => isPhotoSelected(photo)) || [];

  const finishedFilesKeys = finishedFiles.map((photo) => {
    return { key: photo.key, providerRef: photo.providerRef };
  });
  const albumContentWithoutFreshDownload = albumContent.filter(
    (albumPhoto) => isPhotoInPicturesList(albumPhoto, finishedFilesKeys) === false
  );
  const albumContentSelected =
    albumContentWithoutFreshDownload.filter((photo) => isPhotoSelected(photo)) || [];

  const photoSelection = (fileList: SelectionPhoto[], deselectAction: boolean) => {
    for (const photo of fileList) {
      if (isPhotoSelected(photo) === deselectAction) {
        onSelect(photo);
      }
    }
  };
  const handleClickSelectAll = (fileList: SelectionPhoto[]) =>
    maxSelectableItems
      ? photoSelection(fileList.slice(0, maxSelectableItems), false)
      : photoSelection(fileList, false);
  const handleClickDeselectAll = (fileList: SelectionPhoto[]) => photoSelection(fileList, true);

  return (
    <div className="UploadSummary">
      <div className="UploadSummary-section">
        <div className="UploadSummary-section-head">
          <span className="UploadSummary-section-head-title">
            {intl.formatMessage({ id: "kustom_UploadSummary_today" })}
          </span>
          {finishedFilesSelected.length === 0 && (
            <span
              className="UploadSummary-section-head-action"
              onClick={() => handleClickSelectAll(finishedFiles)}
            >
              {intl.formatMessage({ id: "kustom_UploadSummary_select" })} ({finishedFiles.length})
            </span>
          )}
          {finishedFilesSelected.length > 0 && (
            <span
              className="UploadSummary-section-head-action"
              onClick={() => handleClickDeselectAll(finishedFiles)}
            >
              {intl.formatMessage({ id: "kustom_UploadSummary_deselect" })} (
              {finishedFilesSelected.length})
            </span>
          )}
        </div>
        <div className="UploadSummary-section-content">
          <AnimatePresence>
            {uploadProvider && (
              <SelectionUploadButton onFileChange={onFileChange}>
                {intl.formatMessage({ id: "kustom_Selection_buttonLabel_upload" })}
              </SelectionUploadButton>
            )}

            {loading && (
              <motion.div
                animate={{ opacity: 1 }}
                className="UploadSummary-section-content-loading"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                key="uploading"
                layout
                transition={{ duration: 0.2 }}
              >
                <Player
                  autoplay={true}
                  loop={true}
                  renderer="svg"
                  src={iconSpinner}
                  style={{
                    height: "30px",
                    width: "30px",
                  }}
                />
              </motion.div>
            )}

            {/* todo: should separate SelectionProviderBrowser in albums vs photolist to manage this case ? */}
            {finishedFiles.length > 0 &&
              finishedFiles.map((selectionPhoto) => {
                return (
                  <motion.div key={selectionPhoto.key} layout>
                    <SelectionProviderBrowserPhoto
                      onSelect={onSelect}
                      selected={isPhotoSelected(selectionPhoto)}
                      selectionPhoto={selectionPhoto}
                    />
                  </motion.div>
                );
              })}
          </AnimatePresence>
        </div>
      </div>
      {albumContentWithoutFreshDownload.length > 0 && (
        <div className="UploadSummary-section">
          <div className="UploadSummary-section-head">
            <span className="UploadSummary-section-head-title">
              {intl.formatMessage({ id: "kustom_UploadSummary_previously" })}
            </span>
            {albumContentSelected.length === 0 && (
              <span
                className="UploadSummary-section-head-action"
                onClick={() => handleClickSelectAll(albumContentWithoutFreshDownload)}
              >
                {intl.formatMessage({ id: "kustom_UploadSummary_select" })} (
                {albumContentWithoutFreshDownload.length})
              </span>
            )}
            {albumContentSelected.length > 0 && (
              <span
                className="UploadSummary-section-head-action"
                onClick={() => handleClickDeselectAll(albumContentWithoutFreshDownload)}
              >
                {intl.formatMessage({ id: "kustom_UploadSummary_deselect" })} (
                {albumContentSelected.length})
              </span>
            )}
          </div>
          <div className="UploadSummary-section-content">
            {albumContentWithoutFreshDownload.map((selectionPhoto) => {
              return (
                <SelectionProviderBrowserPhoto
                  key={selectionPhoto.key}
                  onSelect={onSelect}
                  selected={isPhotoSelected(selectionPhoto)}
                  selectionPhoto={selectionPhoto}
                />
              );
            })}
          </div>
          {pagination.display ? <SelectionPagination onClick={pagination.onClick} /> : null}
        </div>
      )}
    </div>
  );
};

export default UploadSummary;
